import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ParametresTraversesDoublePoutre,
  StructureTraversesDoublePoutre,
  TypeMotifTraverseDoublePoutre,
  TypeMateriauTraverseDoublePoutre,
  TraversesDoublePoutreState,
  DonneesStructureDoublePoutre,
  PARAMETRES_DEFAUT_TRAVERSES_DOUBLE_POUTRE,
  CONTRAINTES_TRAVERSES_DOUBLE_POUTRE,
  MotifPersonnaliseDoublePoutre
} from '../types';
import { TraversesDoublePoutre3DService } from '../services/TraversesDoublePoutre3DService';
import { Canvas2DDoublePoutre } from './Canvas2DDoublePoutre';
import './TraversesDoublePoutreModule.css';

interface TraversesDoublePoutreModuleProps {
  donneesStructureDoublePoutre: DonneesStructureDoublePoutre;
  onTraversesChange?: (structure: StructureTraversesDoublePoutre | null) => void;
  onError?: (error: string) => void;
}

/**
 * Module des traverses pour la double poutre
 * Composant React responsable de l'interface utilisateur et de la coordination des services
 */
function TraversesDoublePoutreModule({
  donneesStructureDoublePoutre,
  onTraversesChange,
  onError
}: TraversesDoublePoutreModuleProps) {
  // États du composant
  const [state, setState] = useState<TraversesDoublePoutreState>({
    parametres: PARAMETRES_DEFAUT_TRAVERSES_DOUBLE_POUTRE,
    isGenerating: false,
    canvasActif: false
  });
  
  // État pour l'éditeur Canvas 2D
  const [canvasVisible, setCanvasVisible] = useState(false);
  const [motifPersonnalise, setMotifPersonnalise] = useState<MotifPersonnaliseDoublePoutre | undefined>();

  // Références
  const serviceRef = useRef<TraversesDoublePoutre3DService | null>(null);

  // Synchronisation avec la structure de la double poutre
  useEffect(() => {
    if (donneesStructureDoublePoutre) {
      // Convertir le matériau de la structure de base vers le type traverses
      const materiauTraverse = donneesStructureDoublePoutre.materiau === 'tube_carre' 
        ? TypeMateriauTraverseDoublePoutre.TUBE_CARRE 
        : TypeMateriauTraverseDoublePoutre.FER_A_BETON;
      
      // Synchroniser l'épaisseur et la couleur des traverses avec celle de la structure de base
      // Conversion de mm (structure de base) vers mètres (traverses)
      const epaisseurStructureEnMetres = donneesStructureDoublePoutre.epaisseur / 1000;
      
      // Utiliser une épaisseur plus fine pour les traverses de la double poutre
      const epaisseurFinale = Math.max(epaisseurStructureEnMetres * 0.5, 0.01); // 50% de l'épaisseur principale, minimum 1cm
      const couleurGlobale = donneesStructureDoublePoutre.couleur || '#8B4513';
      
      // Vérifier si les paramètres ont vraiment changé avant de mettre à jour
      setState(prevState => {
        const currentParams = prevState.parametres;
        if (currentParams.materiau !== materiauTraverse || 
            Math.abs(currentParams.epaisseur - epaisseurFinale) > 0.001 ||
            currentParams.couleur !== couleurGlobale) {
          return {
            ...prevState,
            parametres: {
              ...prevState.parametres,
              materiau: materiauTraverse,
              epaisseur: epaisseurFinale,
              couleur: couleurGlobale
            }
          };
        }
        return prevState;
      });
    }
  }, [donneesStructureDoublePoutre]);

  // Initialisation des services
  useEffect(() => {
    if (!serviceRef.current) {
      serviceRef.current = new TraversesDoublePoutre3DService();
    }

    return () => {
      if (serviceRef.current) {
        serviceRef.current.dispose();
        serviceRef.current = null;
      }
    };
  }, []);

  // Références pour les callbacks
  const onTraversesChangeRef = useRef(onTraversesChange);
  const onErrorRef = useRef(onError);
  
  // Mettre à jour les références quand les props changent
  useEffect(() => {
    onTraversesChangeRef.current = onTraversesChange;
    onErrorRef.current = onError;
  });

  /**
   * Génère les traverses avec les paramètres actuels
   */
  const genererTraverses = useCallback(async (parametresAUtiliser: ParametresTraversesDoublePoutre) => {
    if (!serviceRef.current) return;



      setState(prev => ({ ...prev, isGenerating: true, error: undefined }));

      try {
        // Génération de la structure des traverses
        const nouvelleStructure = serviceRef.current.genererTraverses(
          parametresAUtiliser,
          donneesStructureDoublePoutre
        );
        



      setState(prev => ({ ...prev, structure: nouvelleStructure, isGenerating: false }));
      onTraversesChangeRef.current?.(nouvelleStructure);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';


      setState(prev => ({ ...prev, error: errorMessage, isGenerating: false }));
      onErrorRef.current?.(errorMessage);
    }
  }, [donneesStructureDoublePoutre]);

  // Référence pour éviter les boucles infinies
  const isGeneratingRef = useRef(false);
  const previousParametresRef = useRef<ParametresTraversesDoublePoutre | null>(null);
  const previousStructureRef = useRef<any>(null);

  // Génération automatique lors des changements
  useEffect(() => {
    if (isGeneratingRef.current) {
      return;
    }
    
    const currentParams = state.parametres;
    const previousParams = previousParametresRef.current;
    const previousStructure = previousStructureRef.current;
    
    // Vérifier changements paramètres
    const parametresChanged = !previousParams || 
        previousParams.active !== currentParams.active ||
        previousParams.motif !== currentParams.motif ||
        previousParams.nombreRangees !== currentParams.nombreRangees ||
        previousParams.espacementVertical !== currentParams.espacementVertical ||
        previousParams.hauteurPosition !== currentParams.hauteurPosition ||
        previousParams.epaisseur !== currentParams.epaisseur ||
        previousParams.couleur !== currentParams.couleur ||
        previousParams.materiau !== currentParams.materiau;
    
    // Vérifier changements structure de base
    const structureChanged = !previousStructure ||
        previousStructure.largeur !== donneesStructureDoublePoutre.largeur ||
        previousStructure.longueur !== donneesStructureDoublePoutre.longueur ||
        previousStructure.hauteur !== donneesStructureDoublePoutre.hauteur ||
        previousStructure.materiau !== donneesStructureDoublePoutre.materiau ||
        previousStructure.forme !== donneesStructureDoublePoutre.forme ||
        previousStructure.nombreMontants !== donneesStructureDoublePoutre.nombreMontants ||
        previousStructure.positionsMontants?.length !== donneesStructureDoublePoutre.positionsMontants?.length;
    
    if (!parametresChanged && !structureChanged) {
      return;
    }
    
    // Sauvegarder les paramètres et structure actuels
    previousParametresRef.current = { ...currentParams };
    previousStructureRef.current = { ...donneesStructureDoublePoutre };
    
    if (currentParams.active && serviceRef.current) {
      isGeneratingRef.current = true;
      genererTraverses(currentParams).finally(() => {
        isGeneratingRef.current = false;
      });
    } else if (!currentParams.active && serviceRef.current) {
      onTraversesChangeRef.current?.(null);
    }
  }, [donneesStructureDoublePoutre, state.parametres, genererTraverses]);

  /**
   * Met à jour un paramètre spécifique
   */
  const mettreAJourParametre = useCallback(<K extends keyof ParametresTraversesDoublePoutre>(
    cle: K,
    valeur: ParametresTraversesDoublePoutre[K]
  ) => {
    setState(prev => ({
      ...prev,
      parametres: {
        ...prev.parametres,
        [cle]: valeur
      }
    }));
  }, []);

  // Gestionnaires pour l'éditeur Canvas 2D
  const ouvrirEditeur2D = useCallback(() => {
    setCanvasVisible(true);
  }, []);

  const fermerEditeur2D = useCallback(() => {
    setCanvasVisible(false);
  }, []);

  const gererChangementMotif = useCallback((motif: MotifPersonnaliseDoublePoutre) => {
    setMotifPersonnalise(motif);
    mettreAJourParametre('motifPersonnalise', motif);
  }, [mettreAJourParametre]);

  return (
    <div className="traverses-double-poutre-module">
      <div className="module-header">
        <h4>Traverses de la Double Poutre</h4>
        <p className="module-description">
          Configuration des traverses décoratives pour la double poutre
        </p>
      </div>

      <div className="module-content">
        {/* Activation */}
        <div className="controle-groupe">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={state.parametres.active}
              onChange={(e) => mettreAJourParametre('active', e.target.checked)}
            />
            <span>Activer les traverses</span>
          </label>
        </div>

        {/* Paramètres (visibles seulement si activé) */}
        {state.parametres.active && (
          <>
            {/* Sélection du motif */}
            <div className="controle-groupe">
              <label className="controle-label">Motif des traverses :</label>
              <select
                value={state.parametres.motif}
                onChange={(e) => {
                  const nouveauMotif = e.target.value as TypeMotifTraverseDoublePoutre;
                  mettreAJourParametre('motif', nouveauMotif);
                }}
                className="controle-select"
              >
                <option value={TypeMotifTraverseDoublePoutre.SIMPLE}>Simple</option>
                <option value={TypeMotifTraverseDoublePoutre.VAGUE_DOUCE}>Vague douce</option>
                <option value={TypeMotifTraverseDoublePoutre.LOSANGE}>Losange</option>
                <option value={TypeMotifTraverseDoublePoutre.PERSONNALISE}>Personnalisé</option>
              </select>
            </div>

            {/* Nombre de rangées */}
            <div className="controle-groupe">
              <label className="controle-label">
                Nombre de rangées : {state.parametres.nombreRangees}
              </label>
              <input
                type="range"
                min={CONTRAINTES_TRAVERSES_DOUBLE_POUTRE.nombreRangees.min}
                max={CONTRAINTES_TRAVERSES_DOUBLE_POUTRE.nombreRangees.max}
                step={CONTRAINTES_TRAVERSES_DOUBLE_POUTRE.nombreRangees.step}
                value={state.parametres.nombreRangees}
                onChange={(e) => mettreAJourParametre('nombreRangees', parseInt(e.target.value))}
                className="controle-slider"
              />
            </div>

            {/* Espacement vertical */}
            <div className="controle-groupe">
              <label className="controle-label">
                Espacement vertical : {state.parametres.espacementVertical.toFixed(2)}m
              </label>
              <input
                type="range"
                min={CONTRAINTES_TRAVERSES_DOUBLE_POUTRE.espacementVertical.min}
                max={CONTRAINTES_TRAVERSES_DOUBLE_POUTRE.espacementVertical.max}
                step={CONTRAINTES_TRAVERSES_DOUBLE_POUTRE.espacementVertical.step}
                value={state.parametres.espacementVertical}
                onChange={(e) => mettreAJourParametre('espacementVertical', parseFloat(e.target.value))}
                className="controle-slider"
              />
            </div>

            {/* Position verticale */}
            <div className="controle-groupe">
              <label className="controle-label">
                Position verticale : {(state.parametres.hauteurPosition * 100).toFixed(0)}%
              </label>
              <input
                type="range"
                min={CONTRAINTES_TRAVERSES_DOUBLE_POUTRE.hauteurPosition.min}
                max={CONTRAINTES_TRAVERSES_DOUBLE_POUTRE.hauteurPosition.max}
                step={CONTRAINTES_TRAVERSES_DOUBLE_POUTRE.hauteurPosition.step}
                value={state.parametres.hauteurPosition}
                onChange={(e) => mettreAJourParametre('hauteurPosition', parseFloat(e.target.value))}
                className="controle-slider"
              />
            </div>

            {/* Épaisseur */}
            <div className="controle-groupe">
              <label className="controle-label">
                Épaisseur : {(state.parametres.epaisseur * 100).toFixed(1)}cm
              </label>
              <input
                type="range"
                min={CONTRAINTES_TRAVERSES_DOUBLE_POUTRE.epaisseur.min}
                max={CONTRAINTES_TRAVERSES_DOUBLE_POUTRE.epaisseur.max}
                step={CONTRAINTES_TRAVERSES_DOUBLE_POUTRE.epaisseur.step}
                value={state.parametres.epaisseur}
                onChange={(e) => mettreAJourParametre('epaisseur', parseFloat(e.target.value))}
                className="controle-slider"
              />
            </div>

            {/* Éditeur 2D pour motifs personnalisés */}
            {state.parametres.motif === TypeMotifTraverseDoublePoutre.PERSONNALISE && (
              <div className="editeur-section">
                <h5>Éditeur de motif personnalisé</h5>
                
                <div className="editeur-controles">
                  <button
                    onClick={ouvrirEditeur2D}
                    className="bouton-editeur"
                  >
                    🎨 Ouvrir l'éditeur 2D
                  </button>
                  
                  {motifPersonnalise && (
                    <div className="motif-info">
                      <span>Motif: {motifPersonnalise.nom}</span>
                      <span>Éléments: {motifPersonnalise.lignes.length + motifPersonnalise.courbes.length}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Messages d'état */}
        {state.isGenerating && (
          <div className="message-info">
            Génération des traverses en cours...
          </div>
        )}

        {state.error && (
          <div className="message-erreur">
            Erreur : {state.error}
          </div>
        )}


      </div>
      
      {/* Éditeur Canvas 2D */}
      <Canvas2DDoublePoutre
        motifPersonnalise={motifPersonnalise}
        onMotifChange={gererChangementMotif}
        onClose={fermerEditeur2D}
        visible={canvasVisible}
      />
    </div>
  );
}

export { TraversesDoublePoutreModule };